/*  Yarn  */
import {
    addDays,
    addMonths,
    addQuarters,
    addWeeks,
    format,
    getMonth,
    startOfDay,
    startOfMonth,
    startOfQuarter,
    startOfWeek,
    subMonths
} from 'date-fns';

import { ReportTimeRange } from '../types/reports';

/**
 * Utility functions for scheduled reports
 */

/**
 * Calculates the next delivery time for a scheduled report based on its period
 * @param dateCreated - The date the report was created (used for customQuarter calculations)
 * @param period - The report period ('daily', 'weekly', 'monthly', 'quarterly', 'customQuarter')
 * @returns Date object representing the next delivery time, or null if invalid period
 */
export const calculateNextDeliveryTime = ({
    dateCreated,
    period
}: {
    dateCreated: string | Date;
    period: string;
}): Date | null => {
    const today = startOfDay(new Date());

    switch (period) {
        case 'daily': {
            // Daily reports run at the start of each day
            return addDays(today, 1);
        }

        case 'weekly': {
            // Weekly reports run on Mondays
            const nextMonday = startOfWeek(addWeeks(today, 1), {
                weekStartsOn: 1
            });
            return nextMonday;
        }

        case 'monthly': {
            // Monthly reports run on the 1st of each month
            const nextMonth = startOfMonth(addMonths(today, 1));
            return nextMonth;
        }

        case 'quarterly': {
            // Quarterly reports run on the first day of each quarter
            const nextQuarter = startOfQuarter(addQuarters(today, 1));
            return nextQuarter;
        }

        case 'customQuarter': {
            // Custom Quarterly reports run on the first day of the mod 3 of the start time of original report
            const reportsMonth = getMonth(new Date(dateCreated));

            const todaysMonthPlus1 = startOfMonth(addMonths(today, 1));
            if (getMonth(todaysMonthPlus1) % 3 === reportsMonth % 3) {
                return todaysMonthPlus1;
            }
            const todaysMonthPlus2 = startOfMonth(addMonths(today, 2));
            if (getMonth(todaysMonthPlus2) % 3 === reportsMonth % 3) {
                return todaysMonthPlus2;
            }
            return startOfMonth(addMonths(today, 3));
        }

        default:
            return null;
    }
};

/**
 * Formats the next delivery time for display
 * @param dateCreated - The date the report was created (used for customQuarter calculations)
 * @param period - The report period ('daily', 'weekly', 'monthly', 'quarterly', 'customQuarter')
 * @returns Formatted string of the next delivery time in YYYY/MM/DD format
 * @throws Error if period is invalid
 */
export const formatNextDeliveryTime = ({
    dateCreated,
    period
}: {
    dateCreated: string | Date;
    period: string;
}): string => {
    const nextDelivery = calculateNextDeliveryTime({
        dateCreated,
        period
    });

    if (!nextDelivery) {
        throw new Error(
            `Invalid period: ${period}. Expected one of: daily, weekly, monthly, customQuarter, quarterly`
        );
    }

    return format(nextDelivery, 'yyyy/MM/dd');
};

/**
 * Calculates the time range for a scheduled report based on its period
 * @param dateCreated - The date the report was created (used for customQuarter calculations)
 * @param period - The report period ('daily', 'weekly', 'monthly', 'quarterly', 'customQuarter')
 * @returns ReportTimeRange object containing startTime and endTime timestamps
 * @throws Error if period is invalid
 */
export const calculateScheduledReportTimeRange = ({
    dateCreated,
    period
}: {
    dateCreated: string | Date;
    period: string;
}): ReportTimeRange => {
    const today = startOfDay(new Date());

    switch (period) {
        case 'daily': {
            const startTime = startOfDay(new Date()).getTime();
            const endTime = new Date().getTime();
            return { endTime, startTime };
        }

        case 'weekly': {
            // Weekly reports run on Mondays
            const startTime = startOfWeek(today, {
                weekStartsOn: 1
            }).getTime();
            const endTime = new Date().getTime();
            return { endTime, startTime };
        }

        case 'monthly': {
            // Monthly reports run on the 1st of each month
            const startTime = startOfMonth(new Date()).getTime();
            const endTime = new Date().getTime();
            return { endTime, startTime };
        }

        case 'quarterly': {
            // Quarterly reports run on the first day of each quarter
            const startTime = startOfQuarter(new Date()).getTime();
            const endTime = new Date().getTime();
            return { endTime, startTime };
        }

        case 'customQuarter': {
            // Custom Quarterly reports run on the first day of the mod 3 of the start time of original report
            const reportsMonth = getMonth(new Date(dateCreated));

            // If the report goes out next month, then we need to get data from the this current month and the previous two
            const todaysMonthPlus1 = startOfMonth(addMonths(new Date(), 1));
            if (getMonth(todaysMonthPlus1) % 3 === reportsMonth % 3) {
                return {
                    endTime: new Date().getTime(),
                    startTime: startOfMonth(subMonths(new Date(), 2)).getTime()
                };
            }

            // If the report goes out in two months, then we need to get data from the this current month and the previous one
            const todaysMonthPlus2 = startOfMonth(addMonths(today, 2));
            if (getMonth(todaysMonthPlus2) % 3 === reportsMonth % 3) {
                return {
                    endTime: new Date().getTime(),
                    startTime: startOfMonth(subMonths(new Date(), 1)).getTime()
                };
            }

            // Else, the report goes out in three months, i.e., it went out this month,
            // then we need to get data from the this current month only
            return {
                endTime: new Date().getTime(),
                startTime: startOfMonth(new Date()).getTime()
            };
        }

        default:
            return { endTime: undefined, startTime: undefined };
    }
};
